<script lang="ts">
	import { untrack } from 'svelte'
	import {
		Icon,
		BaseInput,
		ButtonSize,
		MarkdownInput,
		Button,
		ButtonType,
		ToastType,
		Chip,
		Modal,
		DetailedSelect,
		OutputObjectPromptVariantsForm,
		OutputObjectConstraintPromptForm
	} from '@repo/commons/components'
	import {
		OUTPUT_OBJECT_VOICE_PROMPT_TOOLBAR_CONFIG,
		showToast,
		swapPosOfTwoArrayItems,
		validateForm,
		validateEmptyString,
		printStyledJson,
		replaceNewlinesWithSpaces,
		getConstraintNamesFromKey,
		buildOutputObjectDefaults
	} from '@repo/commons/utils'
	import {
		type OutputObject,
		Modality,
		SupportedLanguage,
		type Constraint
	} from '@semanticedge/persistence-service-noheaders-client/src'
	import { toastsStore } from '@repo/commons/store'
	import { t } from 'svelte-i18n'
	import { DEFAULT_CONSTRAINT_NAME } from '@repo/commons/constants'
	import {
		ProjectViewDialog,
		type OutputObjectPromptVariantsRequest,
		type OutputObjectPromptVariantsResponse,
		type FormValidationItem,
		type ConstraintSetIds,
		type OutputObjectPrompts,
		type DetailedSelectItem,
		type ProjectViewModel,
		type ValidatorFunctionFactory
	} from '@repo/commons/models'

	const KEY_SEPERATOR: string = ', '

	type VisibilityKey = 'addNewPromptButton'

	type VisibilityConfig = Partial<Record<VisibilityKey, boolean>>

	const DEFAULT_VISIBILITY: Record<VisibilityKey, boolean> = {
		addNewPromptButton: true
	}

	/** When 'modal', prompt variants are edited in an internal Modal. When 'inline', only onPromptVariantsRequest is called and the parent shows the variant UI (e.g. switch screen). */
	type PromptVariantsEditMode = 'modal' | 'inline'

	interface Props {
		project: ProjectViewModel
		onPromptVariantsRequest: (request: OutputObjectPromptVariantsRequest) => void
		showToastOnErrors: boolean
		outputObject?: Partial<OutputObject>
		defaultName?: string
		nameReadOnly?: boolean
		createNameValidator?: (initialName?: string) => ValidatorFunctionFactory
		selectedModality: Modality
		topicIdForLlmGeneration?: string
		visibility?: VisibilityConfig
		highlightPattern?: string
		onHighlightMatch?: (match: string, isExactMatch: boolean) => void | null
		/** Controls how prompt variants editing is shown: internal Modal (default) or inline (parent handles via onPromptVariantsRequest). */
		promptVariantsEditMode?: PromptVariantsEditMode
	}

	let {
		project,
		highlightPattern,
		onPromptVariantsRequest,
		showToastOnErrors = true,
		onHighlightMatch = () => null,
		outputObject = $bindable(),
		defaultName,
		nameReadOnly = false,
		createNameValidator,
		topicIdForLlmGeneration = $bindable(),
		visibility = DEFAULT_VISIBILITY,
		selectedModality,
		promptVariantsEditMode = 'modal'
	}: Props = $props()

	const { supportedLanguages, supportedModalities } = project!.metadata

	let isShowingVariantModal = $state(false)
	let isShowingConstraintPromptModal = $state(false)
	let promptVariantsRequest = $state<OutputObjectPromptVariantsRequest | null>(null)

	if (!outputObject || Object.keys(outputObject).length === 0) {
		outputObject = buildOutputObjectDefaults(
			project!.constraints,
			supportedModalities,
			supportedLanguages
		)
	}
	let validatedOutputObject = outputObject
	let nameValidator = $state(createNameValidator?.(outputObject.name))
	if (!outputObject.name && defaultName) outputObject.name = defaultName
	$effect(() => {
		const currentOutputObject = outputObject
		if (validatedOutputObject === currentOutputObject) return

		validatedOutputObject = currentOutputObject
		nameValidator = untrack(() => createNameValidator?.(currentOutputObject.name))
	})

	let touchedFields: Record<string, boolean> = $state({})
	let errors: Record<string, string> = $state({})
	const initialLanguage: SupportedLanguage = supportedLanguages.includes(
		project!.metadata?.defaultLanguage as SupportedLanguage
	)
		? (project!.metadata?.defaultLanguage as SupportedLanguage)
		: (supportedLanguages[0] ?? (project!.metadata?.defaultLanguage as SupportedLanguage))
	let selectedLanguage = $state(initialLanguage)

	const validationSchema: Record<string, FormValidationItem> = $derived.by(() => {
		const schema: Record<string, FormValidationItem> = {}
		if (!nameReadOnly) {
			schema.name = {
				formComponent: outputObject.name ?? '',
				validators: nameValidator ? [validateEmptyString, nameValidator] : [validateEmptyString],
				strictlyRequired: true
			}
		}

		for (const key of promptKeys) {
			for (const lang of supportedLanguages) {
				const fieldKey = `${key}__${lang}`

				const constraintNames = getConstraintNamesFromKey(constraints, key)
				const constraintsText: string = constraintNames.join(', ')
				const promptRequiredKey =
					constraintNames.length === 1
						? 'OutputObjectForm.errors.promptRequired'
						: 'OutputObjectForm.errors.promptRequiredMultipleConstraints'
				const promptValueByModality: Record<Modality, string> = supportedModalities.reduce(
					(acc: Record<Modality, string>, modality: Modality) => {
						acc[modality] = outputObject.prompts?.[key]?.[modality]?.[lang]?.[0] ?? ''
						return acc
					},
					{} as Record<Modality, string>
				)

				schema[fieldKey] = {
					formComponent: promptValueByModality,
					validators: [
						((constraintsValue: string, languageValue: SupportedLanguage) => {
							return (customError?: string, payload?: Record<string, string>) => {
								const mergedPayload: Record<string, string> = {
									constraints: constraintsValue,
									language: String(languageValue),
									modality: payload?.modality ?? selectedModality,
									...(payload ?? {})
								}
								const effectiveKey: string = customError ?? promptRequiredKey
								return validateEmptyString(effectiveKey, mergedPayload)
							}
						})(constraintsText, lang)
					],
					strictlyRequired: false,
					translationKeyName: key
				}
			}
		}

		return schema
	})

	const constraints: Constraint[] = $derived.by(() => project!.constraints)

	const promptKeys: string[] = $derived.by(() => {
		return (
			outputObject.orderedConstraintSetIds
				?.map((set: string[]) => `[${set.join(KEY_SEPERATOR)}]`)
				?.filter((key: string) => outputObject.prompts?.[key]?.[selectedModality]) ?? []
		)
	})

	const languageOptions: DetailedSelectItem[] = $derived.by(() => {
		let langs = [...supportedLanguages]
		const defaultLanguage = project?.metadata?.defaultLanguage
		const isDefaultLanguageSupported = !!defaultLanguage && langs.includes(defaultLanguage)
		return langs.map((language) => ({
			id: language,
			title: language,
			disabled: language === selectedLanguage,
			description:
				language === defaultLanguage && isDefaultLanguageSupported ? 'Default language' : undefined
		}))
	})

	const selectedLanguageOption = $derived.by(() => {
		return languageOptions.find((option) => option.id === selectedLanguage)
	})

	export const validate = (isSubmitting: boolean = false): Record<string, string> => {
		if (isSubmitting) touchedFields.name = true
		const validationErrors = validateForm(validationSchema, touchedFields, isSubmitting)
		if (isSubmitting) errors = validationErrors

		if (showToastOnErrors && validationErrors && Object.keys(validationErrors).length > 0) {
			Object.entries(validationErrors).forEach(([key, error]: [string, string]) => {
				showToast(toastsStore, {
					message: $t(error),
					type: ToastType.ERROR
				})
			})
		}

		return validationErrors
	}
	const handleInput = (key: string, value: string) => {
		if (!outputObject.prompts![key][selectedModality][selectedLanguage]) {
			outputObject.prompts![key][selectedModality][selectedLanguage] = []
		}
		outputObject.prompts![key][selectedModality][selectedLanguage][0] = value
		printStyledJson(`key: ${key}, value: ${value} => outputObject`, $state.snapshot(outputObject))
	}

	const handleBlur = (key: string) => {
		touchedFields[key] = true
	}

	const handleSwitchToNewPromptView = () => {
		isShowingConstraintPromptModal = true
	}

	const applyNewPrompt = (constraintIds: string[], promptText: string): void => {
		const sorted = [...constraintIds].sort()
		const sortedConstraintIdsStr: ConstraintSetIds = `[${sorted.join(KEY_SEPERATOR)}]`

		const prompts = outputObject.prompts!
		prompts[sortedConstraintIdsStr] ??= {}
		for (const modality of supportedModalities) {
			prompts[sortedConstraintIdsStr][modality] ??= {}
			for (const lang of supportedLanguages) {
				// Only the selected modality + selected language get the new prompt; keep existing prompts elsewhere.
				const isSelectedSlot = modality === selectedModality && lang === selectedLanguage
				if (isSelectedSlot) {
					prompts[sortedConstraintIdsStr][modality][lang] = [promptText]
				} else {
					prompts[sortedConstraintIdsStr][modality][lang] ??= ['']
				}
			}
		}

		const isAlreadyOrdered = outputObject.orderedConstraintSetIds!.some(
			(set: string[]) => `[${[...set].sort().join(KEY_SEPERATOR)}]` === sortedConstraintIdsStr
		)
		if (!isAlreadyOrdered) {
			outputObject.orderedConstraintSetIds!.push(sorted)
		}
	}

	const handleConstraintPromptAdded = (payload: {
		constraintIds: string[]
		promptText: string
	}): void => {
		applyNewPrompt(payload.constraintIds, payload.promptText)
		isShowingConstraintPromptModal = false
	}

	const movePromptUp = (index: number) => {
		if (index > 0) {
			outputObject.orderedConstraintSetIds = [
				...swapPosOfTwoArrayItems(outputObject.orderedConstraintSetIds!, index, index - 1)
			]
		}
	}

	const movePromptDown = (index: number) => {
		if (index < promptKeys.length - 1) {
			outputObject.orderedConstraintSetIds = [
				...swapPosOfTwoArrayItems(outputObject.orderedConstraintSetIds!, index, index + 1)
			]
		}
	}

	const handlePromptDeleted = (key: string) => {
		delete outputObject.prompts![key][selectedModality]
		outputObject.orderedConstraintSetIds = outputObject.orderedConstraintSetIds!.filter(
			(set: string[]) => `[${set.join(KEY_SEPERATOR)}]` !== key
		)
		console.log(`Deleting key: ${key} => outputObject.prompts now: ${outputObject.prompts}`)
	}

	const handleTranslateClicked = (key: string) => {}

	const handleSettingsClicked = (key: string) => {
		const request: OutputObjectPromptVariantsRequest = {
			dialogType: ProjectViewDialog.OUTPUT_OBJECT,
			prompts: outputObject.prompts as OutputObjectPrompts,
			selectedConstraintIds: key,
			language: selectedLanguage,
			selectedModality: selectedModality
		} as OutputObjectPromptVariantsRequest

		if (promptVariantsEditMode === 'modal') {
			isShowingVariantModal = true
			promptVariantsRequest = { ...request }
		}

		onPromptVariantsRequest(request)
	}

	const getPromptVariants = (key: string): string[] =>
		outputObject.prompts?.[key]?.[selectedModality]?.[selectedLanguage] ?? ['']

	const containsDefaultConstraint = (key: string) => {
		return getConstraintNamesFromKey(constraints, key).includes(DEFAULT_CONSTRAINT_NAME)
	}

	$effect(() => {
		errors = validate()
	})
</script>

<div class="output-object-form">
	<BaseInput
		label={$t('Item.outputObjectName')}
		value={outputObject.name ?? ''}
		disabled={nameReadOnly}
		errorMessage={errors.name}
		showError={!!errors.name}
		onInput={(value: string) => {
			outputObject.name = value
			touchedFields.name = true
		}}
		onBlur={() => (touchedFields.name = true)}
	/>
	<div class="form-field">
		<DetailedSelect
			label="Language"
			items={languageOptions}
			selectedItem={selectedLanguageOption}
			onSelect={(item: DetailedSelectItem) => {
				selectedLanguage = item.id
			}}
			{highlightPattern}
			{onHighlightMatch}
		/>
	</div>

	{#each promptKeys as key, index}
		{#if outputObject.prompts?.[key][selectedModality] && getPromptVariants(key).length > 0}
			<div class="form-field">
				<div class="prompt-container">
					<div class="prompt-controls">
						<div class="chevron-group">
							<Button
								buttonSize={ButtonSize.EXTRA_SMALL}
								buttonType={ButtonType.TERTIARY_INVERSE}
								buttonText=""
								rounded
								iconProps={{ name: 'chevronUp', size: 'md', class: 'text-[#002C4D80]' }}
								onclick={() => {
									if (index !== 0) movePromptUp(index)
								}}
							/>
							<Button
								buttonSize={ButtonSize.EXTRA_SMALL}
								buttonType={ButtonType.TERTIARY_INVERSE}
								buttonText=""
								rounded
								iconProps={{ name: 'chevronDown', size: 'md', class: 'text-[#002C4D80]' }}
								onclick={() => {
									if (index !== promptKeys.length - 1) movePromptDown(index)
								}}
							/>
						</div>
						<div class="flex items-center gap-2">
							{#if !containsDefaultConstraint(key)}
								<Button
									buttonSize={ButtonSize.EXTRA_SMALL}
									buttonType={ButtonType.TERTIARY_INVERSE}
									buttonText=""
									rounded
									iconProps={{ name: 'trash', size: 'md', class: 'text-[#002C4D80]' }}
									onclick={() => handlePromptDeleted(key)}
								/>
							{/if}
							<Button
								buttonSize={ButtonSize.EXTRA_SMALL}
								buttonType={ButtonType.TERTIARY_INVERSE}
								buttonText=""
								rounded
								iconProps={{ name: 'translate', size: 'md', class: 'text-[#002C4D80]' }}
								onclick={() => handleTranslateClicked(key)}
							/>
							<Button
								buttonSize={ButtonSize.EXTRA_SMALL}
								buttonType={ButtonType.TERTIARY_INVERSE}
								buttonText=""
								rounded
								iconProps={{ name: 'settingsOutline', size: 'md', class: 'text-[#002C4D80]' }}
								onclick={() => handleSettingsClicked(key)}
							/>
						</div>
					</div>
					{#if selectedModality === Modality.Voice}
						{@const chips = getConstraintNamesFromKey(constraints, key)}
						{@const promptCount = getPromptVariants(key).length.toString()}
						<MarkdownInput
							allowLineBreaks={false}
							toolbarConfig={OUTPUT_OBJECT_VOICE_PROMPT_TOOLBAR_CONFIG}
							value={replaceNewlinesWithSpaces(
								outputObject.prompts[key]?.[selectedModality]?.[selectedLanguage]?.[0] || ''
							)}
							enableImageUpload={true}
							onInput={(value: string) => handleInput(key, replaceNewlinesWithSpaces(value))}
							onBlur={() => handleBlur(key)}
							errorMessage={errors[key]}
							showError={!!errors[key]}
						>
							{#if chips || promptCount}
								<div class="bottom-row">
									{#if getConstraintNamesFromKey(constraints, key)}
										{@const chips = getConstraintNamesFromKey(constraints, key)}
										<div class="chips-container">
											{#each chips as chip}
												<Chip text={chip} removable={false} />
											{/each}
										</div>
									{/if}
									{#if promptCount}
										<div class="bottom-right-text">{promptCount}</div>
									{/if}
								</div>
							{/if}
						</MarkdownInput>
					{:else if selectedModality === Modality.Chat}
						{@const chips = getConstraintNamesFromKey(constraints, key)}
						{@const promptCount = getPromptVariants(key).length.toString()}
						<div class="markdown-input-container">
							<MarkdownInput
								value={outputObject.prompts[key][selectedModality][selectedLanguage]?.[0] || ''}
								enableImageUpload={true}
								onInput={(value: string) => handleInput(key, value)}
								onBlur={() => handleBlur(key)}
								errorMessage={errors[key]}
							>
								{#if chips || promptCount}
									<div class="bottom-row">
										{#if chips}
											<div class="chips-container">
												{#each chips as chip}
													<Chip text={chip} removable={false} />
												{/each}
											</div>
										{/if}
										{#if promptCount}
											<div class="bottom-right-text">{promptCount}</div>
										{/if}
									</div>
								{/if}
							</MarkdownInput>
						</div>
					{/if}
				</div>
			</div>
		{/if}
	{/each}
	{#if visibility.addNewPromptButton}
		<div class="form-actions">
			<Button
				onclick={handleSwitchToNewPromptView}
				buttonText={$t('OutputObjectForm.addNewPrompt')}
				buttonType={ButtonType.NEUTRAL}
				fillWidth={true}
			/>
		</div>
	{/if}
</div>

{#if promptVariantsEditMode === 'modal'}
	<Modal
		title="Prompt variants"
		visible={isShowingVariantModal}
		onclose={() => (isShowingVariantModal = false)}
		showCloseButton={false}
		closeOnBackdropClick={false}
	>
		<div class="variant-form-wrapper">
			{#if promptVariantsRequest}
				<OutputObjectPromptVariantsForm
					{project}
					{promptVariantsRequest}
					onPromptVariantsResponse={(response: OutputObjectPromptVariantsResponse) => {
						outputObject.prompts = response.prompts
						promptVariantsRequest = null
						isShowingVariantModal = false
					}}
					onClose={() => {
						isShowingVariantModal = false
						promptVariantsRequest = null
					}}
				/>
			{/if}
		</div>
	</Modal>
{/if}

<Modal
	title="Constraints"
	visible={isShowingConstraintPromptModal}
	showCloseButton={false}
	closeOnBackdropClick={false}
>
	<OutputObjectConstraintPromptForm
		constraints={project!.constraints}
		prompts={outputObject.prompts as OutputObjectPrompts}
		{selectedModality}
		{selectedLanguage}
		{supportedLanguages}
		onAddPrompt={handleConstraintPromptAdded}
		onclose={() => (isShowingConstraintPromptModal = false)}
	/>
</Modal>

<style lang="postcss">
	.output-object-form {
		@apply flex flex-col gap-6;
	}

	.form-field {
		@apply flex flex-col;
	}

	.form-actions {
		@apply w-full;
	}

	.add-prompt-section {
		@apply flex flex-col gap-4;
	}

	.variant-form-wrapper {
		@apply min-w-0 max-w-full w-[434px];
	}

	.error-message {
		@apply text-red-600 text-sm mt-1;
	}

	.prompt-container {
		@apply flex flex-col gap-1;
	}

	.prompt-controls {
		@apply flex justify-end items-center gap-2;
	}

	.chevron-group {
		@apply flex flex-row gap-0;
	}

	.markdown-input-container {
		@apply flex flex-col;
	}

	.bottom-row {
		@apply flex justify-between items-start border-t border-[#002C4D80]/20 bg-gray-50 px-2 py-4;
	}

	.chips-container {
		@apply flex flex-wrap gap-2;
		max-width: 70%;
	}

	.bottom-right-text {
		@apply text-sm text-gray-500 whitespace-nowrap;
	}
</style>
